import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, Send, MessageSquare, CalendarDays } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CALENDLY_DEMO_URL, getWhatsAppUrl } from "@/constants/links";

// lucide's brand icons are deprecated, so WhatsApp ships as an inline SVG.
const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

export default function ContactUs() {
  const { user } = useAuth();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const send = async () => {
    if (!subject.trim() || !message.trim()) return toast.error("Subject and message are required");
    setBusy(true);
    try {
      await api.post("/contact", { subject: subject.trim(), message: message.trim() });
      toast.success("Message sent to NEIV support");
      setSent(true);
      setSubject("");
      setMessage("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send message");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="contact-us-page">
      <div>
        <span className="overline">Support</span>
        <h1 className="mt-3 text-4xl md:text-5xl font-display font-semibold tracking-tight">Contact Us</h1>
        <p className="mt-2 text-[#5C5F62]">Question, bug, or feedback? We're here to help.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Info cards */}
        <div className="space-y-4">
          {[
            {
              icon: MessageSquare,
              title: "Chat with us",
              body: "Send a message and we'll reply to your registered email within 24 hours.",
            },
            {
              icon: WhatsAppIcon,
              title: "WhatsApp us",
              body: "Prefer chat? Message our support team directly on WhatsApp.",
              link: getWhatsAppUrl(
                `Hi NEIV team! I'm ${user?.business_name || "a NEIV user"} and I have a question.`
              ),
              linkLabel: "Chat on WhatsApp →",
              whatsapp: true,
            },
            {
              icon: Mail,
              title: "Email support",
              body: "Reply to any NEIV email or write to our support address directly.",
            },

            {
              icon: CalendarDays,
              title: "Book a Demo",
              body: "Want a live walkthrough? Schedule a 30-min demo with our team.",
              link: CALENDLY_DEMO_URL,
            },
          ].map(({ icon: Icon, title, body, link, linkLabel, whatsapp }) => (
            <div key={title} className="neivai-card p-5 flex items-start gap-4">
              <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${whatsapp ? "bg-[#25D366]/10" : "bg-[#F3F1EC]"}`}>
                <Icon className={`h-4 w-4 ${whatsapp ? "text-[#25D366]" : "text-[#C86B53]"}`} strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm font-medium text-[#1A1C1E]">{title}</p>
                <p className="mt-0.5 text-xs text-[#8A8D91] leading-relaxed">{body}</p>
                {link && (
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`mt-2 inline-block text-xs font-medium hover:underline ${whatsapp ? "text-[#25D366]" : "text-[#C86B53]"}`}
                  >
                    {linkLabel || "Schedule now →"}
                  </a>
                )}
              </div>
            </div>
          ))}

          {/* Sender info */}
          <div className="neivai-card p-5">
            <p className="text-xs uppercase tracking-widest text-[#8A8D91] mb-3">Sending as</p>
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-[#C86B53]/10 flex items-center justify-center shrink-0">
                <span className="text-sm font-semibold text-[#C86B53]">
                  {user?.business_name?.[0]?.toUpperCase() || "?"}
                </span>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{user?.business_name}</p>
                <p className="text-xs text-[#8A8D91] truncate">{user?.email}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-2 neivai-card p-6 md:p-8">
          {sent ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="h-14 w-14 rounded-full bg-[#F3F1EC] flex items-center justify-center mb-4">
                <Send className="h-6 w-6 text-[#C86B53]" />
              </div>
              <h3 className="text-xl font-display font-medium">Message sent!</h3>
              <p className="mt-2 text-sm text-[#5C5F62] max-w-xs">
                We'll get back to you at <strong>{user?.email}</strong> within 1 business day.
              </p>
              <button
                onClick={() => setSent(false)}
                className="mt-6 neivai-btn-secondary text-sm"
              >
                Send another message
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              <h3 className="text-xl font-display font-medium">Send a message</h3>

              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[#8A8D91] block mb-1.5">Subject</label>
                <input
                  data-testid="contact-subject-input"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="What's this about?"
                  maxLength={200}
                  className="w-full bg-white border border-[#E5E5E5] rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C86B53]/20 focus:border-[#C86B53] transition"
                />
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[#8A8D91] block mb-1.5">Message</label>
                <textarea
                  data-testid="contact-message-input"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Describe your question or issue in detail…"
                  maxLength={5000}
                  rows={8}
                  className="w-full bg-white border border-[#E5E5E5] rounded-md px-4 py-2.5 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#C86B53]/20 focus:border-[#C86B53] transition"
                />
                <p className="mt-1 text-right text-xs text-[#8A8D91]">{message.length}/5000</p>
              </div>

              <div className="flex items-center justify-end pt-1">
                <button
                  data-testid="contact-send-btn"
                  onClick={send}
                  disabled={busy || !subject.trim() || !message.trim()}
                  className="neivai-btn-primary inline-flex items-center gap-2 disabled:opacity-60"
                >
                  {busy ? "Sending…" : <><Send className="h-4 w-4" /> Send message</>}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
